import React, { createContext, type PropsWithChildren, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
   DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE,
   isSupplyChainRiskClassificationScale,
   type SupplyChainRiskClassificationScale,
} from './riskClassification';
import useOrganisationContext from 'context/OrganisationContext/hook';
import axiosClient from 'utils/axiosAPIClient';

const riskClassificationScaleResponseSchema = z
   .looseObject({
      mediumThreshold: z.number(),
      highThreshold: z.number(),
      revision: z.number().int().nonnegative(),
      canUpdate: z.boolean(),
      updatedAt: z.iso.datetime().nullish(),
   })
   .superRefine((value, context) => {
      if (isSupplyChainRiskClassificationScale(value)) return;
      context.addIssue({ code: 'custom', message: 'Invalid Supply Chain risk classification scale.' });
   });

type RiskClassificationScaleResponse = z.infer<typeof riskClassificationScaleResponseSchema>;
type SaveRiskClassificationScaleResult = { status: 'saved' } | { status: 'discarded' };

interface ScopedRiskClassificationScaleResponse extends RiskClassificationScaleResponse {
   organisationId: number;
}

interface RiskClassificationScaleContextValue {
   scale: SupplyChainRiskClassificationScale;
   revision: number;
   canUpdate: boolean;
   isLoading: boolean;
   error: unknown;
   refresh: () => Promise<void>;
   saveScale: (scale: SupplyChainRiskClassificationScale, justification: string) => Promise<SaveRiskClassificationScaleResult>;
}

const defaultValue: RiskClassificationScaleContextValue = {
   scale: DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE,
   revision: 0,
   canUpdate: false,
   isLoading: false,
   error: null,
   refresh: async () => undefined,
   saveScale: async () => ({ status: 'discarded' }),
};

const RiskClassificationScaleContext = createContext<RiskClassificationScaleContextValue>(defaultValue);

function parseResponse(value: unknown): RiskClassificationScaleResponse {
   const result = riskClassificationScaleResponseSchema.safeParse(value);
   if (!result.success) throw new TypeError('Invalid Supply Chain risk classification scale response', { cause: result.error });
   return result.data;
}

export function RiskClassificationScaleProvider({ children }: PropsWithChildren) {
   const {
      state: { activeOrganisationId },
   } = useOrganisationContext();
   const [response, setResponse] = useState<ScopedRiskClassificationScaleResponse | null>(null);
   const [isLoading, setIsLoading] = useState(Boolean(activeOrganisationId));
   const [loadError, setLoadError] = useState<{ organisationId: number; error: unknown } | null>(null);
   const requestSequence = useRef(0);
   const requestController = useRef<AbortController | null>(null);
   const activeOrganisationIdRef = useRef(activeOrganisationId);

   useEffect(() => {
      activeOrganisationIdRef.current = activeOrganisationId;
   }, [activeOrganisationId]);

   const refresh = useCallback(async () => {
      const requestId = ++requestSequence.current;
      requestController.current?.abort();

      if (!activeOrganisationId) {
         requestController.current = null;
         setResponse(null);
         setLoadError(null);
         setIsLoading(false);
         return;
      }

      const controller = new AbortController();
      requestController.current = controller;
      setLoadError(null);
      setIsLoading(true);

      try {
         const result = await axiosClient.get('/v2/supplychain/risk-classification-scale', {
            params: { organisationId: activeOrganisationId },
            signal: controller.signal,
         });
         if (requestSequence.current !== requestId) return;
         setResponse({ ...parseResponse(result.data), organisationId: activeOrganisationId });
      } catch (requestError) {
         if (requestSequence.current !== requestId) return;
         setLoadError({ organisationId: activeOrganisationId, error: requestError });
      } finally {
         if (requestSequence.current === requestId) {
            requestController.current = null;
            setIsLoading(false);
         }
      }
   }, [activeOrganisationId]);

   useEffect(() => {
      void refresh();
      return () => {
         requestSequence.current += 1;
         requestController.current?.abort();
         requestController.current = null;
      };
   }, [refresh]);

   const activeResponse = response?.organisationId === activeOrganisationId ? response : null;
   const activeError = loadError && loadError.organisationId === activeOrganisationId ? loadError.error : null;
   const isActiveOrganisationLoading = Boolean(activeOrganisationId && (isLoading || (!activeResponse && !activeError)));

   const saveScale = useCallback(
      async (scale: SupplyChainRiskClassificationScale, justification: string): Promise<SaveRiskClassificationScaleResult> => {
         if (!activeOrganisationId) throw new Error('Select an organisation before saving the classification scale.');
         if (!activeResponse) throw new Error('Wait for the active organisation classification scale to load before saving.');

         const organisationId = activeOrganisationId;
         const requestId = requestSequence.current;
         const result = await axiosClient.put(
            '/v2/supplychain/risk-classification-scale',
            { ...scale, justification, expectedRevision: activeResponse.revision },
            { params: { organisationId } }
         );
         if (requestSequence.current !== requestId || activeOrganisationIdRef.current !== organisationId) return { status: 'discarded' };
         setResponse({ ...parseResponse(result.data), organisationId });
         setLoadError(null);
         return { status: 'saved' };
      },
      [activeOrganisationId, activeResponse]
   );

   const value = useMemo<RiskClassificationScaleContextValue>(
      () => ({
         scale: activeResponse
            ? { mediumThreshold: activeResponse.mediumThreshold, highThreshold: activeResponse.highThreshold }
            : DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE,
         revision: activeResponse?.revision ?? 0,
         canUpdate: activeResponse?.canUpdate ?? false,
         isLoading: isActiveOrganisationLoading,
         error: activeError,
         refresh,
         saveScale,
      }),
      [activeError, activeResponse, isActiveOrganisationLoading, refresh, saveScale]
   );

   return <RiskClassificationScaleContext.Provider value={value}>{children}</RiskClassificationScaleContext.Provider>;
}

export const useRiskClassificationScale = () => use(RiskClassificationScaleContext);
